import Radium from '@flipdish/radium';
import Dialog from '@material-ui/core/Dialog';
import * as React from 'react';
import { getTranslate, TranslateFunction } from 'react-localize-redux';
import { connect } from 'react-redux';
import * as Actions from '../../actions/Actions';
import * as Api from '../../Api';
import * as Data from '../../Data';
import * as Store from '../../Store';
import { getAppConfig } from '../../selectors/basketSelectors';

const tinycolor = require('tinycolor2');

interface DispatchProps {
  SetConsentPermission: (consent: boolean) => any;
}

interface StateProps {
  termsLink: string;
  privacyLink: string;
  Customer: Data.Async<Api.Customer>;
  AppDesignScheme: Api.AppDesignScheme;
  isDark: boolean;
  translate: TranslateFunction;
}

type Props = StateProps & DispatchProps;

interface State {
  ShowModal: boolean;
}

class PermissionConsentDialog extends React.Component<Props, State> {
  constructor(props: Props, context: any) {
    super(props, context);
    this.state = {
      ShowModal: null
    };
  }

  public onConfirm = () => {
    console.log('Permission Consent Confirmed');
    this.props.SetConsentPermission(true);
    this.setState({
      ShowModal: false
    });
  };

  public onDeny = () => {
    console.log('Permission Consent Denied');
    this.props.SetConsentPermission(false);
    this.setState({
      ShowModal: false
    });
  };

  public showModal = (): boolean => {
    if (this.state.ShowModal === null) {
      const { Customer } = this.props;
      if (Customer.status != 'success') {
        return false;
      }

      return Customer.data.ConsentRequest.RequestConsent;
    } else {
      return this.state.ShowModal;
    }
  };

  public bodyText = (part: string): string => {
    const { Customer } = this.props;
    if (Customer.status != 'success') {
      return '';
    }

    switch (part) {
      case 'MessageText':
        return Customer.data.ConsentRequest.MessageText;
      case 'ConfirmButtonText':
        return Customer.data.ConsentRequest.ConfirmButtonText;
      case 'DenyButtonText':
        return Customer.data.ConsentRequest.DenyButtonText;
      case 'SmallPrintText':
        return Customer.data.ConsentRequest.SmallPrintText;
      case 'SmallPrintUrl':
        return Customer.data.ConsentRequest.SmallPrintUrl;
    }
  };

  public render(): JSX.Element {
    const { AppDesignScheme, isDark, translate } = this.props;

    const styles = {
      primary: {
        color: AppDesignScheme.Primary
      },
      tertiary: {
        color: AppDesignScheme.Tertiary
      },
      buttonSecondary: {
        backgroundColor: AppDesignScheme.Secondary,
        ':hover': {},
        ':active': {},
        ':focus': {}
      },
      buttonActive: {
        backgroundColor: tinycolor(AppDesignScheme.Secondary)
          .darken(10)
          .toString()
      },
      buttonHover: {
        backgroundColor: tinycolor(AppDesignScheme.Secondary)
          .darken(5)
          .toString()
      }
    };

    return (
      <Dialog
        open={this.showModal()}
        className={`permission-consent ${isDark ? 'permission-consent--dark' : ''}`}
        disableBackdropClick={true}
        disableEscapeKeyDown={true}
      >
        <div style={{ overflow: 'hidden' }}>
          <div
            role="button"
            tabIndex={0}
            className="permission-consent__title tertiary"
            style={isDark ? styles.primary : styles.tertiary}
          >
            {this.bodyText('MessageText')}
          </div>
          <div className="permission-consent__btn--container">
            <button
              aria-label={`${translate('Yes_option_in')}`}
              className={`fd-button fd-button--secondary`}
              onClick={this.onConfirm}
              key="confirm-button"
              data-fd="permission-confirm"
              style={
                [
                  styles.buttonSecondary,
                  Radium.getState(this.state, 'confirm-button', ':active')
                    ? styles.buttonActive
                    : Radium.getState(this.state, 'confirm-button', ':hover')
                    ? styles.buttonHover
                    : null
                ] as any
              }
            >
              <span className="clear-margins primary" style={styles.primary}>
                {this.bodyText('ConfirmButtonText')}
              </span>
            </button>
            <button
              aria-label={`${translate('No_option_out')}`}
              className={`fd-button fd-button--secondary`}
              onClick={this.onDeny}
              key="deny-button"
              data-fd="permission-deny"
              style={
                [
                  styles.buttonSecondary,
                  Radium.getState(this.state, 'deny-button', ':active')
                    ? styles.buttonActive
                    : Radium.getState(this.state, 'deny-button', ':hover')
                    ? styles.buttonHover
                    : null
                ] as any
              }
            >
              <span className="clear-margins primary" style={styles.primary}>
                {this.bodyText('DenyButtonText')}
              </span>
            </button>
            <div
              style={isDark ? styles.primary : styles.tertiary}
              className="permission-consent__print--text tertiary"
            >
              <div style={{ fontSize: '15px' }}>
                {translate('By_opting_in_you_acknowledge_our')}{' '}
                <a
                  style={{ fontSize: '15px' }}
                  href={this.props.privacyLink}
                  className="permission-consent__print--url"
                  target="_blank"
                >
                  {translate('Privacy_policy')}
                </a>
                .
              </div>
            </div>
          </div>
        </div>
      </Dialog>
    );
  }
}

const mapDispatchToProps = (dispatch: any): DispatchProps => {
  return {
    SetConsentPermission: (consent: boolean) => {
      dispatch(Actions.SetConsentPermission(consent));
    }
  };
};

const mapStateToProps = (state: Store.State): StateProps => {
  const config = getAppConfig(state);
  return {
    termsLink: config?.TermsAndConditionsUrl,
    privacyLink: config?.PrivacyPolicyUrl,
    Customer: state.flipdish.Customer,
    AppDesignScheme: state.appDesignScheme,
    isDark: state.flipdish.isDark,
    translate: getTranslate(state.locale)
  };
};

export default connect(mapStateToProps, mapDispatchToProps)(Radium(PermissionConsentDialog));
